import type { Locale } from "@/i18n";

const localeMap: Record<Locale, string> = {
  es: "es-CO",
  en: "en-US",
};

export function formatDate(date: Date | string, locale: Locale = "es", opts?: Intl.DateTimeFormatOptions) {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(localeMap[locale], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...opts,
  }).format(d);
}

export function daysBetween(a: Date | string, b: Date | string = new Date()) {
  const da = typeof a === "string" ? new Date(a) : a;
  const db = typeof b === "string" ? new Date(b) : b;
  const ms = da.getTime() - db.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function relativeTime(date: Date | string, locale: Locale = "es") {
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = (Date.now() - d.getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(localeMap[locale], { numeric: "auto" });
  const ranges: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [3600, "minute"],
    [86400, "hour"],
    [604800, "day"],
    [2592000, "week"],
    [31536000, "month"],
  ];
  for (const [limit, unit] of ranges) {
    if (Math.abs(diff) < limit) {
      const divisor = limit === 60 ? 1 : ranges[ranges.indexOf([limit, unit]) - 1]?.[0] ?? 1;
      return rtf.format(-Math.round(diff / divisor), unit);
    }
  }
  return rtf.format(-Math.round(diff / 31536000), "year");
}
